import React, { useState } from "react";
import { Tabs, Tab, Box, Typography } from "@mui/material";
import StatusLineIcon from "../../assets/icons/StatusLineSymbol";
import SymbolIcon from "../../assets/icons/SymbolIcon";
import TradeIcon from "../../assets/icons/Trade";
import ScalesAndLinesIcon from "../../assets/icons/ScalesAndLinesIcon";
import CanvasIcon from "../../assets/icons/CanvasIcon";
import StatusLineSettings from "./statusLineSettings";
import TradingSettings from "./tradingSettings";
import ScalesAndLinesSettings from "./scalesAndLinesSettings";
import CanvasSettings from "./canvasSettings";
import AlertSettings from "./alertSettings";
import EventsSettings from "./eventsSettings";
import CalendarIcon from "../../assets/icons/CalendarIcon";
import AddAlertIcon from "../../assets/icons/AddAlertIcon";
import { chartSettingsTabPanelSx } from "./chartSettingsLayout";

type Props = {
    viewKey: string;
    symbolTabContent: React.ReactNode;
    initialTab?: number;
};

const CommonChartSettingsTabs = ({ viewKey, symbolTabContent, initialTab = 0 }: Props) => {
    const [tab, setTab] = useState(initialTab);

    const tabStyle = {
        padding: '6px 0px 6px 20px',
        alignItems: "start",
        justifyContent: 'center',
        textTransform: "none",
        color: (theme: any) => theme.palette.text.primary,
        borderRadius: '4px',
        '&.Mui-selected': {
            color: (theme: any) => theme.palette.text.primary,
            backgroundColor: (theme: any) => theme.palette.action.selected,
            '& .tab-label': {
                fontWeight: 600,
            },
        },
    };

    return (
        <Box sx={{ display: "flex", flex: 1 }}>
            <Tabs
                orientation="vertical"
                value={tab}
                onChange={(_, newValue) => setTab(newValue)}
                sx={{
                    borderRight: 1,
                    borderColor: "divider",
                    minWidth: 200,
                    paddingTop: "6px",
                }}
            >
                <Tab
                    value={0}
                    sx={tabStyle}
                    label={
                        <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <SymbolIcon sx={{ width: 28, height: 28 }} />
                            <Typography fontSize="14px" className="tab-label" fontWeight={400}>
                                Symbol
                            </Typography>
                        </span>
                    }
                />
                <Tab
                    value={1}
                    sx={tabStyle}
                    label={
                        <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <StatusLineIcon sx={{ width: 28, height: 28 }} />
                            <Typography fontSize="14px" className="tab-label" fontWeight={400}>
                                Status Line
                            </Typography>
                        </span>
                    }
                />
                <Tab
                    value={2}
                    sx={tabStyle}
                    label={
                        <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <ScalesAndLinesIcon sx={{ width: 28, height: 28 }} />
                            <Typography fontSize="14px" className="tab-label" fontWeight={400}>
                                Scales and lines
                            </Typography>
                        </span>
                    }
                />
                <Tab
                    value={3}
                    sx={tabStyle}
                    label={
                        <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <CanvasIcon sx={{ width: 28, height: 28 }} />
                            <Typography fontSize="14px" className="tab-label" fontWeight={400}>
                                Canvas
                            </Typography>
                        </span>
                    }
                />
                <Tab
                    value={4}
                    sx={tabStyle}
                    label={
                        <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <TradeIcon sx={{ width: 28, height: 28 }} />
                            <Typography fontSize="14px" className="tab-label" fontWeight={400}>
                                Trading
                            </Typography>
                        </span>
                    }
                />
                <Tab
                    value={5}
                    sx={tabStyle}
                    label={
                        <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <AddAlertIcon sx={{ width: 28, height: 28 }} />
                            <Typography fontSize="14px" className="tab-label" fontWeight={400}>
                                Alerts
                            </Typography>
                        </span>
                    }
                />
                <Tab
                    value={6}
                    sx={tabStyle}
                    label={
                        <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <CalendarIcon sx={{ width: 28, height: 28, fontSize: 24}} />
                            <Typography fontSize="14px" className="tab-label" fontWeight={400}>
                                Events
                            </Typography>
                        </span>
                    }
                />
            </Tabs>

            <Box sx={{ flex: 1, padding: 2 }}>
                <Box sx={{ display: 'grid', '& > *': { gridArea: '1 / 1' } }}>
                    <Box sx={{ visibility: tab === 0 ? 'visible' : 'hidden', ...chartSettingsTabPanelSx }}>{symbolTabContent}</Box>
                    <Box sx={{ visibility: tab === 1 ? 'visible' : 'hidden', ...chartSettingsTabPanelSx }}><StatusLineSettings viewKey={viewKey} /></Box>
                    <Box sx={{ visibility: tab === 2 ? 'visible' : 'hidden', ...chartSettingsTabPanelSx }}><ScalesAndLinesSettings viewKey={viewKey} /></Box>
                    <Box sx={{ visibility: tab === 3 ? 'visible' : 'hidden', ...chartSettingsTabPanelSx }}><CanvasSettings viewKey={viewKey} /></Box>
                    <Box sx={{ visibility: tab === 4 ? 'visible' : 'hidden', ...chartSettingsTabPanelSx }}><TradingSettings viewKey={viewKey} /></Box>
                    <Box sx={{ visibility: tab === 5 ? 'visible' : 'hidden', ...chartSettingsTabPanelSx }}><AlertSettings viewKey={viewKey} /></Box>
                    <Box sx={{ visibility: tab === 6 ? 'visible' : 'hidden', ...chartSettingsTabPanelSx }}><EventsSettings viewKey={viewKey} /></Box>
                </Box>
            </Box>
        </Box>
    );
};

export default CommonChartSettingsTabs;